// Legacy named-event emission — preserves the production GA4 key-event
// reports during the schema transition.
//
// Architecture: alongside the new `book_flow_*` schema, the widget emits
// the five legacy named events that prod GA4 has flagged as key events:
//
//   submitted_booking_website        (step 1, form-submit equivalent)
//   fitting_selection_salesforce     (step 2)
//   appointment_selection_salesforce (step 3)
//   reservation_summary_salesforce   (step 4)
//   booking_confirmation             (step 5, conversion)
//
// These were originally GA4 derived events computed from
// `event_name=booking AND step=N`. The derivation only fires for events
// arriving via the legacy Web Container's gtag path; events POSTed to
// sGTM directly do NOT trigger the derivation. Rather than chase that
// gap, we emit the five legacy names directly from the widget. Each is
// already flagged as a key event in prod GA4 (Admin → Key events), so
// they count as conversions on arrival with zero GA4-side config change.
//
// Mapping (new-schema trigger → legacy named event):
//   book_flow_submit                 → submitted_booking_website
//   book_flow_fitting_type_selected  → fitting_selection_salesforce
//   book_flow_time_selected          → appointment_selection_salesforce
//   book_flow_appointment            → reservation_summary_salesforce
//   book_flow_confirm_a_number       → booking_confirmation
//
// (V3 renamed book_flow_contact_info_complete → book_flow_submit,
// book_flow_loc_type_date_selected → book_flow_fitting_type_selected, the
// old book_flow_submit → book_flow_appointment, and book_flow_complete →
// book_flow_confirm_a_number. The legacy step mapping above follows the
// new names.)
//
// Legacy step 4.1 (extended profile fields like junior_fitting, newsletter,
// hand_dominance) is intentionally skipped — the new widget doesn't collect
// most of those fields. The funnel populates correctly without it.
//
// Enabled by default. To disable (e.g., at sunset, or in a dev environment
// that shouldn't emit), set window.CC_WIDGET_CONFIG.legacyDualFire = false
// on the host page.

import type { EventParams } from './events';
import { postToSgtm, getWebSubmissionId } from './dispatch';
import { pingFunnelStep } from './funnel-ping';

interface LegacyState {
  location?: string;
  fitting_selection?: string;
  date_and_time?: string;
  time_zone?: string;
  hand_dominance?: string;
  referral_method_primary?: string;
  referral_method_secondary?: string;
  referral_capture?: 'auto' | 'user_reported';
  junior_fitting?: boolean;
  promo_code?: string;
  store?: string;
  value?: number;
  currency?: string;
}

const STEP_NAMES: Record<string, string> = {
  '1': 'form submitted - website',
  '2': 'fitting selection - salesforce',
  '3': 'appointment selection - salesforce',
  '4': 'reservation summary - salesforce',
  '5': 'booking confirmation - salesforce',
};

const LEGACY_EVENT_NAMES: Record<string, string> = {
  '1': 'submitted_booking_website',
  '2': 'fitting_selection_salesforce',
  '3': 'appointment_selection_salesforce',
  '4': 'reservation_summary_salesforce',
  '5': 'booking_confirmation',
};

const legacyState: LegacyState = {};
const firedSteps = new Set<string>();

/**
 * Capture referral attribution from the parent page URL on first emit.
 * Legacy SF SchedulingApp populated `referral_method_*` from a user-reported
 * "How did you hear about us?" form field. The new widget doesn't ask, so
 * we substitute technical attribution: utm_source/utm_medium with referrer
 * fallback. `referral_capture: 'auto'` flags the substitution so dashboards
 * can distinguish from legacy user-reported data.
 */
function captureReferralOnce(): void {
  if (legacyState.referral_method_primary !== undefined) return;
  if (typeof window === 'undefined') return;

  const params = new URLSearchParams(window.location.search);
  const utmSource = params.get('utm_source');
  const utmMedium = params.get('utm_medium');

  let primary = utmSource ?? undefined;
  if (!primary && document.referrer) {
    try {
      primary = new URL(document.referrer).hostname;
    } catch {
      primary = undefined;
    }
  }

  legacyState.referral_method_primary = primary ?? '(direct)';
  legacyState.referral_method_secondary = utmMedium ?? '(none)';
  legacyState.referral_capture = 'auto';
}

function accumulate(payload: EventParams): void {
  const p = payload as unknown as Record<string, unknown>;

  if (typeof p.location === 'string') legacyState.location = p.location;
  if (typeof p.fitting_selection === 'string') legacyState.fitting_selection = p.fitting_selection;
  if (typeof p.time_zone === 'string') legacyState.time_zone = p.time_zone;
  if (typeof p.hand_dominance === 'string') legacyState.hand_dominance = p.hand_dominance;
  if (typeof p.referral_method_primary === 'string')
    legacyState.referral_method_primary = p.referral_method_primary;
  if (typeof p.referral_method_secondary === 'string')
    legacyState.referral_method_secondary = p.referral_method_secondary;
  if (typeof p.junior_fitting === 'boolean') legacyState.junior_fitting = p.junior_fitting;
  if (typeof p.promo_code === 'string') legacyState.promo_code = p.promo_code;
  if (typeof p.value === 'number') legacyState.value = p.value;
  if (typeof p.currency === 'string') legacyState.currency = p.currency;
  if (typeof p.store === 'string') legacyState.store = p.store;

  // date_and_time = date_selected + time_selected (when both are present in
  // the same event payload). Matches the legacy combined-string format.
  if (typeof p.date_selected === 'string') {
    const time = typeof p.time_selected === 'string' ? p.time_selected : '';
    legacyState.date_and_time = time ? `${p.date_selected}T${time}` : p.date_selected;
  }
}

/**
 * Emit the legacy named event for a given step. Once-per-session per step.
 *
 * Sent via direct POST to sGTM, which forwards to GA4 with the event name
 * intact. Each name is already flagged as a key event in prod GA4, so
 * arrival = conversion count automatically.
 *
 * channelmix_client_id is included so it routes to the GA4 user_property
 * surface as up.channelmix_client_id per MP shape.
 */
function emitLegacyStep(step: string, extra: Record<string, unknown> = {}): void {
  if (firedSteps.has(step)) return;
  firedSteps.add(step);

  const namedEvent = LEGACY_EVENT_NAMES[step];
  if (!namedEvent) return;

  const params: Record<string, unknown> = {
    step: Number(step),
    step_name: STEP_NAMES[step],
    ...legacyState,
    ...extra,
  };

  if (typeof window !== 'undefined') {
    const cmxId = (window.CC_WIDGET_CONFIG ?? {}).channelmixClientId;
    if (cmxId) params.channelmix_client_id = cmxId;
  }

  for (const k of Object.keys(params)) {
    if (params[k] === undefined) delete params[k];
  }

  // Dual emission:
  //   1. Generic `booking` event — preserves legacy funnel reports that
  //      group by step on event_name=booking.
  //   2. Named legacy event (submitted_booking_website, etc.) — each is
  //      flagged as a key event in prod GA4, so arrival counts as a
  //      conversion. Replaces the GA4 derived-event mechanism, which does
  //      not fire on events arriving via the sGTM Measurement Protocol
  //      path (only on Web Container gtag-sourced events).
  postToSgtm('booking', params);
  postToSgtm(namedEvent, params);

  // Server-side mirror to the middleware. Drives the bookings-viewer
  // funnel report; see funnel-ping.ts for rationale.
  pingFunnelStep(getWebSubmissionId(), Number(step), {
    step_name: STEP_NAMES[step],
    a_number:
      typeof extra.confirmation_number === 'string' ? extra.confirmation_number : undefined,
    location: legacyState.location,
    fitting_selection: legacyState.fitting_selection,
    value: legacyState.value,
    currency: legacyState.currency,
  });
}

/**
 * Called by the main dispatch after each new-schema event. Updates the
 * cumulative legacy state and emits legacy events at the right transition
 * points. Enabled by default — set
 * window.CC_WIDGET_CONFIG.legacyDualFire = false to disable.
 */
export function maybeEmitLegacy(payload: EventParams): void {
  if (typeof window === 'undefined') return;
  if (window.CC_WIDGET_CONFIG?.legacyDualFire === false) return;

  captureReferralOnce();
  accumulate(payload);

  switch (payload.event) {
    case 'book_flow_submit':
      // Legacy step 1 historically arrived at GA4 as event_name=booking,
      // step=1 (the Web Container's Booking 1 tag re-fired the dataLayer
      // form_submit as a `booking` event). We emit the same GA4 shape
      // directly via sGTM.
      emitLegacyStep('1', {
        form_id: 'CMwebsubmit',
        form_name: payload.form_name ?? 'CC Booking Widget',
        channelmix_timestamp: new Date().toISOString(),
      });
      break;

    case 'book_flow_fitting_type_selected':
      emitLegacyStep('2');
      break;

    case 'book_flow_time_selected':
      emitLegacyStep('3');
      break;

    case 'book_flow_appointment':
      emitLegacyStep('4');
      break;

    case 'book_flow_confirm_a_number':
      emitLegacyStep('5', {
        confirmation_number: payload.confirmation_number,
        // SMS opt-in defaults to false at conversion time. The new widget
        // collects SMS opt-in on a separate post-booking step; its true
        // value lives on `post_booking_completed` in the new schema.
        sms_reminder: false,
        // waitlisted (legacy field name) carries the user's path through
        // the flow — 'WL' if joined via the no-availability empty state,
        // 'scheduled' otherwise. Closes the legacy `waitlisted` TBD from
        // the schema audit.
        waitlisted: payload.waitlist,
      });
      break;
  }
}

/** Test-only reset. Not used in production. */
export function _resetLegacyState(): void {
  firedSteps.clear();
  for (const k of Object.keys(legacyState)) {
    delete (legacyState as Record<string, unknown>)[k];
  }
}
